import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, ArrowLeft, ArrowRight } from 'lucide-react';
import StepIndicator from '../shared/StepIndicator';
import FileUpload from '../shared/FileUpload';
import logo from '../../assets/img/56.png';

const RegistroPreadmisionPage = ({ setCurrentPage, source }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        // Paso 1: Datos Generales
        modalidad_postulacion: '',
        // Paso 2: Datos Personales
        nombres: '',
        primer_apellido: '',
        segundo_apellido: '',
        ci: '',
        ci_extension: '',
        ci_complemento: '',
        direccion: '',
        fecha_nacimiento: '',
        lugar_nacimiento: '',
        lugar_nacimiento_otro: '',
        sexo: '',
        edad: '',
        telefono: '',
        // Paso 3: Contactos
        familiar_nombres: '',
        familiar_telefono: '',
        familiar_parentesco: '',
        contacto_nombres: '',
        contacto_telefono: '',
        contacto_parentesco: '',
        // Paso 4: Salud
        tipo_sangre: '',
        alergias: '',
        enfermedad_base: '',
        lesiones_otros: '',
        // Paso 5: Archivos (se manejan por separado)
        // Paso 6: Cuenta
        username: '',
        email: '',
        password: '',
        confirm_password: '',
        // Paso 7: Declaración
        acepta_declaracion: false,
    });

    const [paymentMethod, setPaymentMethod] = useState('qr');

    const [files, setFiles] = useState({
        titulo_bachiller_o_certificado: null,
        foto_ci: null,
        comprobante_pago: null,
    });

    const [paymentConfig, setPaymentConfig] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [inscripcionCerrada, setInscripcionCerrada] = useState(false);
    const [mensajeCierre, setMensajeCierre] = useState('');
    const [generatedUsername, setGeneratedUsername] = useState('');
    const [generatingUsername, setGeneratingUsername] = useState(false);

    // Estados para verificación de correo
    const [emailVerified, setEmailVerified] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [sendingCode, setSendingCode] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);

    // Eliminado: Obtener username generado del backend (ahora es manual)

    // Estado para almacenar qué periodos están habilitados
    const [periodosHabilitados, setPeriodosHabilitados] = useState({
        cpa: true,
        paa: true
    });

    // Verificar AMBOS periodos al cargar la página
    useEffect(() => {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

        const verificarPeriodos = async () => {
            try {
                // Verificar CPA
                const resCpa = await fetch(`${API_URL}/auth/verificar-periodo/inscripcion_cpa`);
                const dataCpa = await resCpa.json();

                // Verificar PAA
                const resPaa = await fetch(`${API_URL}/auth/verificar-periodo/inscripcion_paa`);
                const dataPaa = await resPaa.json();

                setPeriodosHabilitados({
                    cpa: dataCpa.permitido,
                    paa: dataPaa.permitido
                });

                // Si AMBOS periodos están cerrados, bloquear el formulario completamente
                if (!dataCpa.permitido && !dataPaa.permitido) {
                    setInscripcionCerrada(true);
                    setMensajeCierre('La acción no se encuentra habilitada en la fecha actual. Por favor, verifique el periodo establecido por Dirección Académica.');
                }
            } catch (err) {
                console.error('Error verificando periodos:', err);
            }
        };

        verificarPeriodos();
    }, []);

    // Verificar periodo específico al cambiar modalidad (refuerzo)
    useEffect(() => {
        if (!formData.modalidad_postulacion) return;

        const esCurso = formData.modalidad_postulacion.toLowerCase().includes('curso');
        const esHabilitado = esCurso ? periodosHabilitados.cpa : periodosHabilitados.paa;

        if (!esHabilitado) {
            setInscripcionCerrada(true);
            setMensajeCierre('La acción no se encuentra habilitada en la fecha actual. Por favor, verifique el periodo establecido por Dirección Académica.');
        } else {
            setInscripcionCerrada(false);
            setMensajeCierre('');
        }
    }, [formData.modalidad_postulacion, periodosHabilitados]);

    // Efecto para autogenerar username basado en nombres y primer_apellido
    useEffect(() => {
        const { nombres, primer_apellido } = formData;

        // Solo disparar si tenemos ambos campos con algo de contenido
        if (nombres.trim() && primer_apellido.trim()) {
            const timeoutId = setTimeout(async () => {
                setGeneratingUsername(true);
                try {
                    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
                    const response = await fetch(`${API_URL}/auth/preview-username`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            nombres: nombres.trim(),
                            primer_apellido: primer_apellido.trim()
                        })
                    });
                    const data = await response.json();
                    if (data.username) {
                        setFormData(prev => ({ ...prev, username: data.username }));
                    }
                } catch (error) {
                    console.error('Error autogenerando username:', error);
                } finally {
                    setGeneratingUsername(false);
                }
            }, 600); // Debounce de 600ms

            return () => clearTimeout(timeoutId);
        }
    }, [formData.nombres, formData.primer_apellido]);

    const steps = [
        'Datos Generales',
        'Datos Personales',
        'Contactos',
        'Salud',
        'Pago',
        'Documentación',
        'Crear Cuenta',
        'Declaración'
    ];

    const departamentos = [
        { label: 'La Paz', value: 'LP' },
        { label: 'Santa Cruz', value: 'SC' },
        { label: 'Cochabamba', value: 'CB' },
        { label: 'Oruro', value: 'OR' },
        { label: 'Potosí', value: 'PT' },
        { label: 'Chuquisaca', value: 'CH' },
        { label: 'Tarija', value: 'TJ' },
        { label: 'Beni', value: 'BN' },
        { label: 'Pando', value: 'PD' },
        { label: 'Otro', value: 'Otro' }
    ];

    const tiposSangre = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

    // Calcular edad automáticamente
    useEffect(() => {
        if (formData.fecha_nacimiento) {
            const today = new Date();
            const birthDate = new Date(formData.fecha_nacimiento);
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            setFormData(prev => ({ ...prev, edad: age.toString() }));
        }
    }, [formData.fecha_nacimiento]);

    // Cargar configuración de pago al montar
    useEffect(() => {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
        fetch(`${API_URL}/auth/payment-config/preadmision`)
            .then(res => res.json())
            .then(data => {
                console.log('Payment config loaded:', data);
                setPaymentConfig(data);

                // Pre-seleccionar método disponible
                if (data.qr_activo === true) {
                    setPaymentMethod('qr');
                } else if (data.banco_activo === true) {
                    setPaymentMethod('transferencia');
                }
            })
            .catch(err => console.error('Error loading payment config:', err));
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Validadores de campos
        let validatedValue = value;

        // Campos que solo aceptan números
        if (['ci', 'telefono', 'familiar_telefono', 'contacto_telefono', 'edad'].includes(name)) {
            validatedValue = value.replace(/[^0-9]/g, '');
        }

        // Campos que solo aceptan letras y espacios
        if (['nombres', 'primer_apellido', 'segundo_apellido', 'familiar_nombres', 'contacto_nombres', 'familiar_parentesco', 'contacto_parentesco'].includes(name)) {
            validatedValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, '');
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : validatedValue
        }));
        // Limpiar error del campo al escribir
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
        // Si cambia el email, resetear la verificación
        if (name === 'email') {
            setEmailVerified(false);
            setCodeSent(false);
            setVerificationCode('');
        }
    };

    // Función para enviar código de verificación
    const sendVerificationCode = async () => {
        setErrors(prev => ({ ...prev, verification: '' }));
        if (!formData.email) {
            setErrors(prev => ({ ...prev, email: 'Ingresa un correo primero' }));
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setErrors(prev => ({ ...prev, email: 'Formato de correo inválido' }));
            return;
        }

        try {
            setSendingCode(true);
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

            const response = await fetch(`${API_URL}/auth/send-verification-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: formData.email })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error al enviar código');
            }

            setCodeSent(true);
        } catch (error) {
            setErrors(prev => ({ ...prev, verification: error.message }));
        } finally {
            setSendingCode(false);
        }
    };

    // Función para verificar el código
    const verifyCode = async () => {
        setErrors(prev => ({ ...prev, verification: '' }));
        if (!verificationCode || verificationCode.length !== 4) {
            setErrors(prev => ({ ...prev, verification: 'El código debe tener 4 dígitos' }));
            return;
        }

        try {
            setVerifyingCode(true);
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

            const response = await fetch(`${API_URL}/auth/verify-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: formData.email, code: verificationCode })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Código incorrecto');
            }

            setEmailVerified(true);
        } catch (error) {
            setErrors(prev => ({ ...prev, verification: error.message }));
        } finally {
            setVerifyingCode(false);
        }
    };

    const handleFileChange = (name, file) => {
        setFiles(prev => ({ ...prev, [name]: file }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateStep = () => {
        const newErrors = {};

        switch (currentStep) {
            case 1: // Datos Generales
                if (!formData.modalidad_postulacion) {
                    newErrors.modalidad_postulacion = 'Selecciona una modalidad';
                }
                break;

            case 2: // Datos Personales
                if (!formData.nombres.trim()) newErrors.nombres = 'Nombres son requeridos';
                if (!formData.primer_apellido.trim()) newErrors.primer_apellido = 'Primer apellido es requerido';
                if (!formData.ci.trim()) newErrors.ci = 'CI es requerido';
                if (!formData.ci_extension) newErrors.ci_extension = 'Extensión de CI es requerida';
                if (!formData.direccion.trim()) newErrors.direccion = 'Dirección es requerida';
                if (!formData.fecha_nacimiento) newErrors.fecha_nacimiento = 'Fecha de nacimiento es requerida';
                if (!formData.lugar_nacimiento) newErrors.lugar_nacimiento = 'Lugar de nacimiento es requerido';
                if (formData.lugar_nacimiento === 'Otro' && !formData.lugar_nacimiento_otro?.trim()) {
                    newErrors.lugar_nacimiento_otro = 'Especifica tu lugar de nacimiento';
                }
                if (!formData.sexo) newErrors.sexo = 'Sexo es requerido';
                if (!formData.telefono.trim()) newErrors.telefono = 'Teléfono es requerido';
                break;

            case 3: // Contactos
                if (!formData.familiar_nombres.trim()) newErrors.familiar_nombres = 'Nombre familiar es requerido';
                if (!formData.familiar_telefono.trim()) newErrors.familiar_telefono = 'Teléfono familiar es requerido';
                if (!formData.familiar_parentesco.trim()) newErrors.familiar_parentesco = 'Parentesco es requerido';
                if (!formData.contacto_nombres.trim()) newErrors.contacto_nombres = 'Nombre contacto es requerido';
                if (!formData.contacto_telefono.trim()) newErrors.contacto_telefono = 'Teléfono contacto es requerido';
                if (!formData.contacto_parentesco.trim()) newErrors.contacto_parentesco = 'Parentesco es requerido';
                break;

            case 4: // Salud
                if (!formData.tipo_sangre) newErrors.tipo_sangre = 'Tipo de sangre es requerido';
                break;

            case 5: // Pago (solo visual, no validación)
                break;

            case 6: // Documentación
                if (!files.titulo_bachiller_o_certificado) newErrors.titulo_bachiller_o_certificado = 'Documento académico es requerido';
                if (!files.foto_ci) newErrors.foto_ci = 'Foto de CI es requerida';
                if (!files.comprobante_pago) newErrors.comprobante_pago = 'Comprobante de pago es requerido';
                break;

            case 7: // Crear Cuenta
                if (!formData.username.trim()) newErrors.username = 'Usuario es requerido';
                if (!formData.email.trim()) newErrors.email = 'Email es requerido';
                else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email inválido';
                if (!emailVerified) newErrors.verification = 'Debes verificar tu correo electrónico';
                if (!formData.password) newErrors.password = 'Contraseña es requerida';
                else {
                    // Validación de contraseña fuerte
                    const pwd = formData.password;
                    const pwdErrors = [];
                    if (pwd.length < 8) pwdErrors.push('mínimo 8 caracteres');
                    if (!/[A-Z]/.test(pwd)) pwdErrors.push('1 mayúscula');
                    if (!/[a-z]/.test(pwd)) pwdErrors.push('1 minúscula');
                    if (!/[0-9]/.test(pwd)) pwdErrors.push('1 número');
                    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(pwd)) pwdErrors.push('1 símbolo');
                    if (pwdErrors.length > 0) {
                        newErrors.password = `La contraseña debe tener: ${pwdErrors.join(', ')}`;
                    }
                }
                if (formData.password !== formData.confirm_password) {
                    newErrors.confirm_password = 'Las contraseñas no coinciden';
                }
                break;

            case 8: // Declaración
                if (!formData.acepta_declaracion) {
                    newErrors.acepta_declaracion = 'Debes aceptar la declaración jurada';
                }
                break;

            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep()) {
            if (currentStep < steps.length) {
                setCurrentStep(prev => prev + 1);
                window.scrollTo(0, 0);
            }
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
            window.scrollTo(0, 0);
        }
    };

    const handleSubmit = async () => {
        if (!validateStep()) return;

        setLoading(true);

        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

            // Convertir archivos a base64 si existen
            const fileToBase64 = (file) => {
                return new Promise((resolve, reject) => {
                    if (!file) {
                        resolve(null);
                        return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
            };

            // Preparar payload completo
            const payload = {
                // Credenciales
                username: formData.username,
                email: formData.email,
                password: formData.password,
                // Datos personales
                modalidad_postulacion: formData.modalidad_postulacion,
                nombres: formData.nombres,
                primer_apellido: formData.primer_apellido,
                segundo_apellido: formData.segundo_apellido || '',
                ci: formData.ci,
                ci_extension: formData.ci_extension,
                ci_complemento: formData.ci_complemento || '',
                direccion: formData.direccion,
                fecha_nacimiento: formData.fecha_nacimiento,
                lugar_nacimiento: formData.lugar_nacimiento === 'Otro'
                    ? formData.lugar_nacimiento_otro
                    : formData.lugar_nacimiento,
                sexo: formData.sexo,
                edad: formData.edad,
                telefono: formData.telefono,
                // Contactos
                familiar_nombres: formData.familiar_nombres,
                familiar_telefono: formData.familiar_telefono,
                familiar_parentesco: formData.familiar_parentesco,
                contacto_nombres: formData.contacto_nombres,
                contacto_telefono: formData.contacto_telefono,
                contacto_parentesco: formData.contacto_parentesco,
                // Datos médicos
                tipo_sangre: formData.tipo_sangre,
                alergias: formData.alergias || '',
                enfermedad_base: formData.enfermedad_base || '',
                lesiones_otros: formData.lesiones_otros || '',
                // Archivos en base64
                titulo_bachiller_o_certificado: await fileToBase64(files.titulo_bachiller_o_certificado),
                foto_ci: await fileToBase64(files.foto_ci),
                comprobante_pago: await fileToBase64(files.comprobante_pago)
            };

            const response = await fetch(`${API_URL}/auth/register/preadmision`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error al registrar');
            }

            // Redirigir a página de éxito
            setCurrentPage('registro-exitoso', { email: formData.email, username: formData.username });
        } catch (error) {
            alert(error.message || 'Error al registrar. Intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800">Datos Generales</h3>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Modalidad de Postulación <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="modalidad_postulacion"
                                value={formData.modalidad_postulacion}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                  ${errors.modalidad_postulacion ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                            >
                                <option value="">Selecciona una modalidad</option>
                                <option value="Curso de Preadmisión">Curso de Preadmisión</option>
                                <option value="Prueba de Admisión Académica">Prueba de Admisión Académica</option>
                                <option value="Admisión Especial">Admisión Especial</option>
                            </select>
                            {errors.modalidad_postulacion && (
                                <p className="mt-1 text-sm text-red-600">{errors.modalidad_postulacion}</p>
                            )}
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800">Datos Personales</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nombres <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="nombres"
                                    value={formData.nombres}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                    ${errors.nombres ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                    placeholder="Tus nombres"
                                />
                                {errors.nombres && <p className="mt-1 text-sm text-red-600">{errors.nombres}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Primer Apellido <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="primer_apellido"
                                    value={formData.primer_apellido}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                    ${errors.primer_apellido ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                    placeholder="Primer apellido"
                                />
                                {errors.primer_apellido && <p className="mt-1 text-sm text-red-600">{errors.primer_apellido}</p>}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Segundo Apellido
                            </label>
                            <input
                                type="text"
                                name="segundo_apellido"
                                value={formData.segundo_apellido}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition"
                                placeholder="Segundo apellido (opcional)"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Cédula de Identidad <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="ci"
                                    value={formData.ci}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                    ${errors.ci ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                    placeholder="Número de CI"
                                />
                                {errors.ci && <p className="mt-1 text-sm text-red-600">{errors.ci}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Extensión <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="ci_extension"
                                    value={formData.ci_extension}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                    ${errors.ci_extension ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                >
                                    <option value="">Ext.</option>
                                    {departamentos.map(dep => (
                                        <option key={dep.value} value={dep.value}>{dep.value}</option>
                                    ))}
                                </select>
                                {errors.ci_extension && <p className="mt-1 text-sm text-red-600">{errors.ci_extension}</p>}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Complemento
                            </label>
                            <input
                                type="text"
                                name="ci_complemento"
                                value={formData.ci_complemento}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition"
                                placeholder="Complemento (si aplica)"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Dirección <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                name="direccion"
                                value={formData.direccion}
                                onChange={handleChange}
                                rows="2"
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                  ${errors.direccion ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                placeholder="Dirección completa"
                            />
                            {errors.direccion && <p className="mt-1 text-sm text-red-600">{errors.direccion}</p>}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Fecha de Nacimiento <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="fecha_nacimiento"
                                    value={formData.fecha_nacimiento}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                    ${errors.fecha_nacimiento ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                />
                                {errors.fecha_nacimiento && <p className="mt-1 text-sm text-red-600">{errors.fecha_nacimiento}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Edad
                                </label>
                                <input
                                    type="text"
                                    value={formData.edad}
                                    disabled
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                                    placeholder="Se calcula automáticamente"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Lugar de Nacimiento <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="lugar_nacimiento"
                                value={formData.lugar_nacimiento}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                  ${errors.lugar_nacimiento ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                            >
                                <option value="">Selecciona un departamento</option>
                                {departamentos.map(dep => (
                                    <option key={dep.value} value={dep.label}>{dep.label}</option>
                                ))}
                            </select>
                            {errors.lugar_nacimiento && <p className="mt-1 text-sm text-red-600">{errors.lugar_nacimiento}</p>}
                        </div>

                        {formData.lugar_nacimiento === 'Otro' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Especifica lugar <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="lugar_nacimiento_otro"
                                    value={formData.lugar_nacimiento_otro}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                      ${errors.lugar_nacimiento_otro ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                    placeholder="Ingresa tu lugar de nacimiento"
                                />
                                {errors.lugar_nacimiento_otro && <p className="mt-1 text-sm text-red-600">{errors.lugar_nacimiento_otro}</p>}
                            </div>
                        )}

                        {/* Campo Sexo */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Sexo <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="sexo"
                                value={formData.sexo}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                                    ${errors.sexo ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                            >
                                <option value="">Selecciona</option>
                                <option value="M">Masculino</option>
                                <option value="F">Femenino</option>
                            </select>
                            {errors.sexo && <p className="mt-1 text-sm text-red-600">{errors.sexo}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Número de Teléfono / Celular <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="tel"
                                name="telefono"
                                value={formData.telefono}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                  ${errors.telefono ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                placeholder="00000000"
                            />
                            {errors.telefono && <p className="mt-1 text-sm text-red-600">{errors.telefono}</p>}
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-gray-800">Contactos</h3>

                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                            <p className="text-sm font-medium text-blue-900">Familiar Cercano</p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nombres y Apellidos <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="familiar_nombres"
                                    value={formData.familiar_nombres}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                    ${errors.familiar_nombres ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                    placeholder="Nombre completo del familiar"
                                />
                                {errors.familiar_nombres && <p className="mt-1 text-sm text-red-600">{errors.familiar_nombres}</p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Teléfono / Celular <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="familiar_telefono"
                                        value={formData.familiar_telefono}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                      ${errors.familiar_telefono ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                        placeholder="00000000"
                                    />
                                    {errors.familiar_telefono && <p className="mt-1 text-sm text-red-600">{errors.familiar_telefono}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Parentesco <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="familiar_parentesco"
                                        value={formData.familiar_parentesco}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                      ${errors.familiar_parentesco ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                        placeholder="Ej: Padre, Madre, Hermano"
                                    />
                                    {errors.familiar_parentesco && <p className="mt-1 text-sm text-red-600">{errors.familiar_parentesco}</p>}
                                </div>
                            </div>
                        </div>

                        <div className="bg-red-50 border-l-4 border-red-500 p-4 mt-6">
                            <p className="text-sm font-medium text-red-900">Contacto de Emergencia</p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nombres y Apellidos <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="contacto_nombres"
                                    value={formData.contacto_nombres}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                    ${errors.contacto_nombres ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                    placeholder="Nombre completo del contacto"
                                />
                                {errors.contacto_nombres && <p className="mt-1 text-sm text-red-600">{errors.contacto_nombres}</p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Teléfono / Celular <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="contacto_telefono"
                                        value={formData.contacto_telefono}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                      ${errors.contacto_telefono ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                        placeholder="00000000"
                                    />
                                    {errors.contacto_telefono && <p className="mt-1 text-sm text-red-600">{errors.contacto_telefono}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Parentesco <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="contacto_parentesco"
                                        value={formData.contacto_parentesco}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                      ${errors.contacto_parentesco ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                        placeholder="Ej: Tío, Primo, Amigo"
                                    />
                                    {errors.contacto_parentesco && <p className="mt-1 text-sm text-red-600">{errors.contacto_parentesco}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800">Información de Salud</h3>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Tipo de Sangre <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="tipo_sangre"
                                value={formData.tipo_sangre}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                  ${errors.tipo_sangre ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                            >
                                <option value="">Selecciona tu tipo de sangre</option>
                                {tiposSangre.map(tipo => (
                                    <option key={tipo} value={tipo}>{tipo}</option>
                                ))}
                            </select>
                            {errors.tipo_sangre && <p className="mt-1 text-sm text-red-600">{errors.tipo_sangre}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Alergias
                            </label>
                            <textarea
                                name="alergias"
                                value={formData.alergias}
                                onChange={handleChange}
                                rows="3"
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition"
                                placeholder="Describe cualquier alergia conocida (opcional)"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Enfermedad de Base
                            </label>
                            <textarea
                                name="enfermedad_base"
                                value={formData.enfermedad_base}
                                onChange={handleChange}
                                rows="3"
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition"
                                placeholder="Describe cualquier enfermedad crónica o condición médica (opcional)"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Lesiones u Otros Vinculados a su Salud
                            </label>
                            <textarea
                                name="lesiones_otros"
                                value={formData.lesiones_otros}
                                onChange={handleChange}
                                rows="3"
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition"
                                placeholder="Describe cualquier lesión física, limitación o información relevante (opcional)"
                            />
                        </div>
                    </div>
                );

            case 5:
                return (
                    <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-gray-800">Pago de Inscripción</h3>

                        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                            <p className="text-sm text-yellow-800">
                                Realiza el pago de inscripción mediante QR o transferencia bancaria
                            </p>
                        </div>

                        {paymentConfig && (
                            <div className="space-y-6">
                                {/* Message if both are disabled */}
                                {paymentConfig.qr_activo === false && paymentConfig.banco_activo === false && (
                                    <div className="bg-rose-50 border-2 border-rose-200 rounded-3xl p-12 text-center animate-in zoom-in duration-300">
                                        <div className="text-6xl mb-6 text-rose-500">💳❌</div>
                                        <h4 className="text-2xl font-black text-rose-900 uppercase tracking-tight mb-4">
                                            Pagos Temporalmente Inhabilitados
                                        </h4>
                                        <p className="text-rose-700 font-medium max-w-sm mx-auto">
                                            Actualmente no se están recibiendo pagos por medios digitales.
                                            Por favor, comunícate con administración para más información.
                                        </p>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* QR Code - Only if active */}
                                    {paymentConfig.qr_activo === true && (
                                        <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                                            <h4 className="font-semibold text-gray-800 mb-4 text-center">Pago con QR</h4>
                                            {paymentConfig.qr_imagen_url ? (
                                                <div className="flex justify-center">
                                                    <img
                                                        src={paymentConfig.qr_imagen_url}
                                                        alt="QR Code"
                                                        className="w-48 h-48 object-contain"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="bg-gray-100 w-48 h-48 mx-auto flex items-center justify-center rounded">
                                                    <p className="text-gray-500 text-sm">QR no disponible</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Transferencia Bancaria - Only if active */}
                                    {paymentConfig.banco_activo === true && (
                                        <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                                            <h4 className="font-semibold text-gray-800 mb-4">Transferencia Bancaria</h4>
                                            <div className="space-y-3 text-sm">
                                                <div>
                                                    <p className="text-gray-600">Banco:</p>
                                                    <p className="font-medium text-gray-900">{paymentConfig.banco_nombre || 'No disponible'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-600">Número de Cuenta:</p>
                                                    <p className="font-medium text-gray-900">{paymentConfig.banco_cuenta || 'No disponible'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-600">Titular:</p>
                                                    <p className="font-medium text-gray-900">{paymentConfig.banco_titular || 'No disponible'}</p>
                                                </div>
                                                {paymentConfig.nit && (
                                                    <div>
                                                        <p className="text-gray-600">NIT:</p>
                                                        <p className="font-medium text-gray-900">{paymentConfig.nit}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800">
                                <strong>Nota:</strong> Asegúrate de guardar el comprobante de pago. Lo necesitarás en el siguiente paso.
                            </p>
                        </div>

                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-sm text-red-800 font-bold mb-2">IMPORTANTE:</p>
                            <ul className="text-sm text-red-800 space-y-2 list-disc list-inside">
                                <li>En el Depósito Bancario debe figurar obligatoriamente: nombre, apellidos y número de cédula de identidad de la o el postulante.</li>
                                <li>Se requiere que el postulante o tutor en caso de menores de edad, se sirva verificar de forma detallada el cumplimiento de la totalidad de los requisitos establecidos previo a la realización del depósito bancario.</li>
                                <li>Esta diligencia es esencial a fin de evitar cualquier observación posterior que pudiera incidir en el proceso.</li>
                                <li>Se hace constar que la EBID no se responsabiliza por errores u omisiones del depositante. En este sentido, no se contempla la devolución por los depósitos realizados.</li>
                            </ul>
                        </div>
                    </div>
                );

            case 6:
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800">Documentación</h3>

                        <FileUpload
                            label="Fotografía de Título de Bachiller O Certificado O Libreta de 6to de Secundaria"
                            name="titulo_bachiller_o_certificado"
                            file={files.titulo_bachiller_o_certificado}
                            onChange={handleFileChange}
                            accept="image/*,.pdf"
                            required
                            error={errors.titulo_bachiller_o_certificado}
                        />

                        <FileUpload
                            label="Fotografía de Cédula de Identidad"
                            name="foto_ci"
                            file={files.foto_ci}
                            onChange={handleFileChange}
                            accept="image/*,.pdf"
                            required
                            error={errors.foto_ci}
                        />

                        <FileUpload
                            label="Fotografía de Cédula de Identidad Tutor (Opcional)"
                            name="foto_ci_tutor"
                            file={files.foto_ci_tutor}
                            onChange={handleFileChange}
                            accept="image/*,.pdf"
                            required={false}
                        />

                        <FileUpload
                            label="Fotografía de Comprobante Bancario del Pago"
                            name="comprobante_pago"
                            file={files.comprobante_pago}
                            onChange={handleFileChange}
                            accept="image/*,.pdf"
                            required
                            error={errors.comprobante_pago}
                        />

                        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mt-4">
                            <p className="text-sm text-yellow-800">
                                <strong>*</strong> La documentación adjunta deberá ser presentada obligatoriamente en formato físico ante el área de Kardex, esto dentro de los plazos establecidos por la institución.
                            </p>
                        </div>
                    </div>
                );

            case 7:
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800">Crear Cuenta</h3>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Usuario <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    name="username"
                                    type="text"
                                    value={formData.username}
                                    readOnly={true}
                                    className={`w-full px-4 py-3 border-2 rounded-lg bg-gray-100 cursor-not-allowed transition
                                        ${errors.username ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder={generatingUsername ? "Generando usuario..." : "Se generará automáticamente"}
                                />
                                {generatingUsername && (
                                    <div className="absolute right-3 top-3.5">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                    </div>
                                )}
                            </div>
                            {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username}</p>}
                            <p className="mt-1 text-xs text-gray-500 font-medium">
                                * El nombre de usuario es asignado obligatoriamente por el sistema basado en sus datos.
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Correo Electrónico <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                disabled={emailVerified}
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                  ${errors.email ? 'border-red-500' : emailVerified ? 'border-green-500 bg-green-50' : 'border-gray-300 focus:border-blue-500'}`}
                                placeholder="tu@email.com"
                            />
                            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contraseña <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 pr-12 border-2 rounded-lg focus:outline-none transition
                    ${errors.password ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                    placeholder="8+ caracteres, mayúscula, número, símbolo"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Confirmar Contraseña <span className="text-red-500">*</span>
                            </label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="confirm_password"
                                value={formData.confirm_password}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                  ${errors.confirm_password ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                placeholder="Repite tu contraseña"
                            />
                            {errors.confirm_password && <p className="mt-1 text-sm text-red-600">{errors.confirm_password}</p>}
                        </div>

                        {/* Sección de verificación de email - AL FINAL */}
                        <div className="mt-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                            <h4 className="text-md font-semibold text-blue-800 mb-3">
                                Verificación de Correo Electrónico
                            </h4>

                            {emailVerified ? (
                                <div className="flex items-center gap-2 text-green-600">
                                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-semibold">¡Correo verificado correctamente!</span>
                                </div>
                            ) : (
                                <>
                                    {!codeSent ? (
                                        <button
                                            type="button"
                                            onClick={sendVerificationCode}
                                            disabled={sendingCode || !formData.email}
                                            className={`w-full py-3 rounded-lg font-semibold transition ${sendingCode || !formData.email
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-blue-900 text-white hover:bg-blue-800'
                                                }`}
                                        >
                                            {sendingCode ? 'Enviando...' : 'Verificar Correo'}
                                        </button>
                                    ) : (
                                        <div className="space-y-3">
                                            <p className="text-sm text-gray-600">
                                                Ingresa el código de 4 dígitos enviado a <strong>{formData.email}</strong>
                                            </p>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    maxLength={4}
                                                    value={verificationCode}
                                                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                                                    placeholder="0000"
                                                    className="flex-1 px-4 py-3 border-2 rounded-lg text-center text-2xl font-bold tracking-widest focus:outline-none focus:border-blue-500"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={verifyCode}
                                                    disabled={verifyingCode || verificationCode.length !== 4}
                                                    className={`px-6 py-3 rounded-lg font-semibold transition ${verifyingCode || verificationCode.length !== 4
                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        : 'bg-green-600 text-white hover:bg-green-700'
                                                        }`}
                                                >
                                                    {verifyingCode ? '...' : 'Verificar'}
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => { setCodeSent(false); setVerificationCode(''); sendVerificationCode(); }}
                                                disabled={sendingCode}
                                                className="text-sm text-blue-600 hover:underline"
                                            >
                                                Reenviar código
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}

                            {errors.verification && (
                                <p className="mt-2 text-sm text-red-600">{errors.verification}</p>
                            )}
                        </div>
                    </div>
                );

            case 8:
                return (
                    <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-gray-800">Declaración Jurada</h3>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-h-96 overflow-y-auto">
                            <p className="text-sm text-gray-700 leading-relaxed text-justify">
                                Yo <strong>{formData.nombres} {formData.primer_apellido} {formData.segundo_apellido}</strong>, hábil por derecho, con Cédula de Identidad N° <strong>{formData.ci} {formData.ci_extension}</strong>, de manera voluntaria acepto los términos establecidos en la Convocatoria y sus procesos de admisión y registro, así mismo declaro en honor a la verdad, que cumplo a cabalidad con los requisitos indispensables exigidos en la convocatoria, siendo que todos los datos proporcionados en el presente formulario son verídicos, por lo que autorizo que la información proporcionada sea verificada y contrastada por la EBID, comprometiéndome a la presentación de la documentación original de acuerdo al cronograma establecido.
                            </p>
                            <p className="text-sm text-gray-700 leading-relaxed text-justify mt-4">
                                Asímismo, asumo plena responsabilidad por cualquier situación relacionada con mi salud o accidentes que pudieran ocurrir durante el desarrollo de mis actividades académicas y extracurriculares en la EBID. En este sentido, autorizo/no autorizo a la EBID que, si fuera necesario, contacte a un familiar o contacto de emergencia cuando sea necesario.
                            </p>
                            <p className="text-sm text-gray-700 leading-relaxed text-justify mt-4">
                                En caso de no estar de acuerdo con lo estipulado, exonero a la institución de toda responsabilidad respecto de accidentes relacionados a mi persona y de salud dentro de la Institución.
                            </p>
                        </div>

                        <div className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                name="acepta_declaracion"
                                checked={formData.acepta_declaracion}
                                onChange={handleChange}
                                className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label className="text-sm text-gray-700">
                                Acepto la declaración jurada y confirmo que toda la información es correcta.
                                <span className="text-red-500 ml-1">*</span>
                            </label>
                        </div>
                        {errors.acepta_declaracion && (
                            <p className="text-sm text-red-600">{errors.acepta_declaracion}</p>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 py-8 px-4">
            <div className="absolute top-0 left-0 w-full h-2 bg-[#1e3a8a]"></div>

            <div className="max-w-3xl mx-auto">
                {/* Botón Volver */}
                <button
                    onClick={() => setCurrentPage(source === 'kardex' ? 'dashboard-kardex' : 'landing')}
                    className="flex items-center gap-2 text-[#1e3a8a] hover:underline font-medium mb-6"
                >
                    <ArrowLeft size={20} />
                    {source === 'kardex' ? 'Volver a Kardex' : 'Volver al inicio'}
                </button>

                {/* Logo y Título */}
                <div className="text-center mb-8">
                    <img
                        src={logo}
                        alt="EBID Logo"
                        className="w-20 h-20 mx-auto mb-4"
                    />
                    <h1 className="text-2xl font-bold text-[#1e3a8a]">
                        Registro de Preadmisión
                    </h1>
                    <p className="text-gray-600 mt-2">
                        Escuela Boliviana Intercultural de Danza
                    </p>
                </div>

                {/* Step Indicator */}
                <StepIndicator currentStep={currentStep} totalSteps={steps.length} steps={steps} />

                {/* Mensaje si inscripciones cerradas */}
                {inscripcionCerrada ? (
                    <div className="bg-white shadow-xl rounded-2xl p-8 mb-6 text-center">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-gray-800 mb-2">Inscripciones Cerradas</h2>
                        <p className="text-gray-600 mb-4">{mensajeCierre}</p>
                        <button
                            onClick={() => setCurrentPage(source === 'kardex' ? 'dashboard-kardex' : 'landing')}
                            className="bg-[#1e3a8a] text-white px-6 py-2 rounded-full hover:bg-[#2c4a9a] transition"
                        >
                            {source === 'kardex' ? 'Volver a Kardex' : 'Volver al inicio'}
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Form Card */}
                        <div className="bg-white shadow-xl rounded-2xl p-8 mb-6">
                            {renderStep()}
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex justify-between gap-4">
                            <button
                                onClick={handlePrevious}
                                disabled={currentStep === 1}
                                className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition
              ${currentStep === 1
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-gray-600 text-white hover:bg-gray-700'}`}
                            >
                                <ArrowLeft size={20} />
                                Anterior
                            </button>

                            {currentStep < steps.length ? (
                                <button
                                    onClick={handleNext}
                                    className="flex items-center gap-2 bg-[#1e3a8a] text-white px-6 py-3 rounded-full hover:bg-[#2c4a9a] transition font-semibold"
                                >
                                    Siguiente
                                    <ArrowRight size={20} />
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className={`bg-green-600 text-white px-8 py-3 rounded-full hover:bg-green-700 transition font-semibold
                ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {loading ? 'Enviando...' : 'Finalizar Registro'}
                                </button>
                            )}
                        </div>
                        {/* Link para usuarios existentes */}
                        <div className="text-center mt-6">
                            <p className="text-sm text-gray-600">
                                ¿Ya tienes una cuenta?{' '}
                                <button
                                    onClick={() => setCurrentPage('login')}
                                    className="text-[#1e3a8a] font-semibold hover:underline"
                                >
                                    Inicia sesión aquí
                                </button>
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RegistroPreadmisionPage;
